import type * as React from "react";
import type { VideoVariant } from "./use-hero-video";

// Figma sizes for each layout, in Figma pixels. --u turns them into real pixels: 1px
// at the design width (430 / 1440), scaled with the hero stage (cqw). On mobile it
// follows the stage up to tablet width (capped at 600px) so it stays in proportion
// with the video; on desktop it never grows past the Figma size.
const CARD_SIZES = {
  mobile: {
    unit: "calc(100cqw / 430)",
    padding: 14.22,
    width: 169.45,
    gap: 11.85,
    textGap: 5.92,
    badgeGap: 7.11,
    square: 7.11,
    label: [12, 12, "-0.01em"],
    title: [18, 18, "-0.01em"],
    weight: 400,
    radius: 9.48,
    shade: "rgba(0, 0, 0, 0.3)",
    blur: 23.7,
  },
  desktop: {
    unit: "min(1px, calc(100cqw / 1440))",
    padding: 16,
    width: 222,
    gap: 12,
    textGap: 8.2,
    badgeGap: 9.84,
    square: 9.84,
    label: [14, 20, "-0.0214em"],
    title: [24, 32, "-0.0208em"],
    weight: 500,
    radius: 12,
    shade: "rgba(0, 0, 0, 0.2)",
    blur: 16,
  },
} as const;

// Callers set display (flex / hidden): a base display class would win over `hidden`
// in Tailwind 4's utility order.
export function OvulationConfirmedCard({
  size,
  className,
}: {
  size: VideoVariant;
  className?: string;
}) {
  const s = CARD_SIZES[size];
  const u = (n: number) => `calc(${n} * var(--u))`;

  return (
    <div
      id={`hero-ovulation-card-${size}`}
      data-testid={`hero-ovulation-card-${size}`}
      className={`flex-col ${className ?? ""}`}
      style={
        {
          "--u": s.unit,
          padding: u(s.padding),
          borderRadius: u(s.radius),
          background: `linear-gradient(167deg, rgba(87, 144, 255, 0.3) 0%, rgba(87, 144, 255, 0) 100%), ${s.shade}`,
          backdropFilter: `blur(${s.blur}px)`,
          WebkitBackdropFilter: `blur(${s.blur}px)`,
        } as React.CSSProperties
      }
    >
      <div className="flex flex-col" style={{ width: u(s.width), gap: u(s.gap) }}>
        <div className="flex flex-col" style={{ gap: u(s.textGap) }}>
          <div className="flex items-center" style={{ gap: u(s.badgeGap) }}>
            <span
              className="shrink-0 bg-[#4783F6]"
              style={{ width: u(s.square), height: u(s.square) }}
            />
            <span
              className="whitespace-nowrap text-white"
              style={{
                fontSize: u(s.label[0]),
                lineHeight: u(s.label[1]),
                letterSpacing: s.label[2],
                fontWeight: s.weight,
              }}
            >
              PdG Rise Detected
            </span>
          </div>
          <span
            className="whitespace-nowrap text-white"
            style={{
              fontSize: u(s.title[0]),
              lineHeight: u(s.title[1]),
              letterSpacing: s.title[2],
              fontWeight: s.weight,
            }}
          >
            Ovulation Confirmed
          </span>
        </div>
        <span
          className="font-medium tracking-[-0.0299em] text-white/60"
          style={{ fontSize: u(12), lineHeight: u(11.85) }}
        >
          Cycle Day 21
        </span>
      </div>
    </div>
  );
}
